import App, { AppProps, AppContext } from 'next/app';
import { DehydratedState, dehydrate } from '@tanstack/react-query';
import { AppInitialProps } from 'next/dist/shared/lib/utils';
import { GetServerSidePropsContext } from 'next';
import { ApiClient } from '@dozuki/web-js/utils';
import { getServerSideApiClient, isInvalidSiteError, LocalizationProvider } from '~/framework';
import { HydratedQueryClientProvider, initQueryClient, prefetchTranslations } from '~/queries';
import { prefetchLoggedInUser } from '~/pages/_shared/useLoggedInUser';
import { prefetchSiteInfo } from '~/pages/_shared';
import { ChakraProvider } from '@chakra-ui/react';
import { theme } from '@dozuki/web-js/theme';
import { ErrorBoundary, ErrorPage, Head, Layout, Pendo } from '~/page-components/Layout';
import { PageProps } from '~/framework/ssr';
import '~/static/fonts/ifixit/v4.5.0/icomoon.css';

// Configure FontAwesome to work with Next.js
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';

// Import global styles
import './Styles/global.css';
import './Styles/prosemirror.css';
import './_shared/fonts.css';
import { NavigationGuardProvider } from 'next-navigation-guard';

config.autoAddCss = false;

type MyAppProps = {
   dehydratedAppState?: DehydratedState;
};

function MyApp({ Component, dehydratedAppState, pageProps }: AppProps<PageProps> & MyAppProps) {
   const { dehydratedPageState, errorStatusCode, ...componentProps } = pageProps;
   return (
      <HydratedQueryClientProvider
         dehydratedPageState={dehydratedPageState}
         dehydratedAppState={dehydratedAppState}
      >
         <Pendo />
         <ChakraProvider theme={theme}>
            <LocalizationProvider>
               <NavigationGuardProvider>
                  <Head />
                  <ErrorBoundary>
                     <Layout>
                        {errorStatusCode ? (
                           <ErrorPage error={{ code: errorStatusCode }} />
                        ) : (
                           <Component {...componentProps} />
                        )}
                     </Layout>
                  </ErrorBoundary>
               </NavigationGuardProvider>
            </LocalizationProvider>
         </ChakraProvider>
      </HydratedQueryClientProvider>
   );
}

MyApp.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps & MyAppProps> => {
   const { ctx } = appContext;
   const initialAppProps = await App.getInitialProps(appContext);

   if (ctx.req) {
      // Server side
      const apiClient = getServerSideApiClient(ctx.req as GetServerSidePropsContext['req']);
      const queryClient = initQueryClient();

      try {
         await Promise.all([
            prefetchTranslations(apiClient, queryClient),
            prefetchSiteInfo(apiClient, queryClient),
            prefetchLoggedInUser(apiClient, queryClient),
         ]);

         const dehydratedAppState = dehydrate(queryClient);

         // Tanstack Query automatically set's cache time to Infinity when used server side,
         // disabling manual garbage collection, and letting node clear everything once a request is
         // complete. However, it's possible that a non-default cache time is set for a query, so we
         // clear it anyway just to be super safe.
         queryClient.clear();

         return {
            ...initialAppProps,
            dehydratedAppState,
         };
      } catch (error) {
         if (!ctx.res) {
            throw error;
         }

         if (ApiClient.isInvalidLoginError(error)) {
            ctx.res.writeHead(307, { Location: '/Login' });
            ctx.res.end();
            return initialAppProps;
         }

         // We expect to only get a 403 response if the user is attempting to access a site from an
         // ip address that is not whitelisted. We simply forward a 403 response to mimic the
         // behavior of the monolith.
         if (ApiClient.isForbiddenError(error)) {
            ctx.res.writeHead(403);
            ctx.res.end();
            return initialAppProps;
         }

         // Redirect to root so that the monolith can display the 'Site Not Found' error page,
         //    or the 'Deactivated Site' error page
         if (isInvalidSiteError(error)) {
            ctx.res.writeHead(307, { Location: '/' });
            ctx.res.end();
            return initialAppProps;
         }

         throw error;
      }
   }

   return initialAppProps;
};

export default MyApp;
